/**
 * @Description 商圈不合适标记组件 - 图标部分
 */
import React, { FC, useContext } from 'react';
import { useMethods } from '@lhb/hook';
import { cancelClusterUnsuitable } from '@/common/api/youth';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import { useBigdataFn } from '@/common/utils/bigdata';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import { CollectListContext } from '@/views/youth/pages/map/ts-config';
import cs from 'classnames';
import styles from './index.module.less';
import IconFont from '@/common/components/IconFont';

/** 不合适标记数据结构 */
export interface UnsuitableMarkType {
  modelClusterId: number; // 商圈ID
  reason: string; // 不合适原因
  operatorId: number; // 操作人ID
  operatorName: string; // 操作人名称
  createdAt: string; // 创建时间
}

interface AreaMarkingProps {
  itemData: {
    id: number; // 商圈ID
    unsuitableMark: UnsuitableMarkType | null; // 不合适标记数据
    [p:string]: any;
  };
  areaName: string;
  className?: any;
  width?: number; // 容器宽度，默认20
  height?: number; // 容器高度，默认20
  imgWidth?: number; // 图片宽度，默认10
  imgHeight?: number; // 图片高度，默认10
  onOpenModal?: (modelClusterId: number) => void; // 打开标记弹窗回调
  onStatusChange?: () => void; // 操作成功回调
  showTooltip?: boolean; // 是否启用Tooltip渲染，默认true
  tooltipPlacement?: TooltipPlacement; // Tooltip弹出方向
}

/** 默认状态图标 */
// const ICON_DEFAULT = `${process.env.STATIC_RES_DOMAIN}/guoquan/map/icon_inappropriate_0@2x.png`;
/** 不合适状态图标 */
// const ICON_UNSUITABLE = `${process.env.STATIC_RES_DOMAIN}/guoquan/map/icon_inappropriate_1@2x.png`;

const AreaMarking: FC<AreaMarkingProps> = ({
  itemData,
  areaName,
  width = 20,
  height = 20,
  imgWidth = 10,
  imgHeight = 10,
  className,
  onOpenModal,
  onStatusChange,
  showTooltip = true,
  tooltipPlacement,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const { unsuitableMarkMap, setUnsuitableMarkMap }: any = useContext(CollectListContext) || {};
  // 合并逻辑：Context中有记录（含null）则优先使用，否则用item自身数据
  const markData: UnsuitableMarkType | null = unsuitableMarkMap && (itemData.id in unsuitableMarkMap)
    ? unsuitableMarkMap[itemData.id]
    : itemData.unsuitableMark;

  const methods = useMethods({
    /** 点击容器，根据当前状态走不同分支 */
    handleClick(e: React.MouseEvent) {
      e.stopPropagation();
      if (markData) {
        // 已标记不合适 -> 弹出确认取消
        methods.handleCancelConfirm();
      } else {
        // 默认状态 -> 通知外层打开标记弹窗
        onOpenModal?.(itemData.id);
      }
    },
    /** 弹出取消标记的二次确认 */
    handleCancelConfirm() {
      V2Confirm({
        title: '提示',
        content: '确定取消不合适标签吗？',
        okText: '保存',
        onSure: (modal: any) => {
          cancelClusterUnsuitable({ modelClusterId: itemData.id }).then(() => {
            modal.destroy();
            bigdataFn('c6c59aa0-1c26-46f3-bf43-83549eea9c84', '商圈取消不合适标记', '取消不合适', { op_block: '商圈取消不合适标记', op_event: `商圈名称：${areaName}`, });

            // 更新Context中的标记状态
            setUnsuitableMarkMap?.((prev: Record<number, any>) => ({ ...prev, [itemData.id]: null }));
            onStatusChange?.();
          });
        },
      });
    },
  });

  // 根据标记状态选择图标
  // const iconSrc = markData ? ICON_UNSUITABLE : ICON_DEFAULT;

  // 容器内容
  const containerNode = (
    <div
      className={cs(styles.container, className)}
      style={{ width, height }}
      onClick={methods.handleClick}
    >
      {/* <img
        src={iconSrc}
        style={{ width: imgWidth, height: imgHeight }}
      /> */}
      <IconFont
        iconHref={markData ? 'iconic_buheshi_1' : 'iconic_buheshi'}
        className={markData ? 'color-danger' : 'c-222'}
        style={{ width: imgWidth, height: imgHeight }}
      />
    </div>
  );

  return (
    <>
      {/* 不合适状态时hover展示原因，默认状态无hover效果 */}
      {markData && showTooltip ? (
        <Tooltip
          title={<span style={{ whiteSpace: 'pre-wrap' }}>{markData.reason}</span>}
          placement={tooltipPlacement}
        >
          {containerNode}
        </Tooltip>
      ) : containerNode}
    </>
  );
};

export default AreaMarking;
